import { useEffect, useRef, useCallback } from 'react';

// Extend Window interface to include FrontChat
declare global {
  interface Window {
    FrontChat?: (command: string, config?: any) => void;
    __frontChatInitialized?: boolean;
  }
}

const FRONT_CHAT_ID = '8ee81b864c7c2acc62265c51cfd0844b';
const FRONT_CHAT_SCRIPT_URL =
  'https://chat-assets.frontapp.com/v1/chat.bundle.js';
const FRONT_CHAT_HIDE_STYLE_ID = 'front-chat-hide-style';

// CSS to force-hide Front Chat launcher
const HIDE_FRONT_CHAT_CSS = `
  #front-chat-container,
  #front-chat-iframe,
  [data-front-chat],
  .front-chat-launcher,
  iframe[src*="frontapp.com"] {
    display: none !important;
    visibility: hidden !important;
    opacity: 0 !important;
    pointer-events: none !important;
  }
`;

/**
 * Custom hook to load and manage Front live chat widget
 *
 * This hook:
 * - Loads the Front chat script dynamically (only once)
 * - Controls visibility based on the `shouldShow` parameter
 * - Uses CSS + API to ensure the launcher is hidden for active customers
 *
 * @param shouldShow - Whether to show the Front chat launcher
 *
 * Usage:
 * ```tsx
 * // Only show for pre-active customers
 * useFrontChat(isPreActive && userRecord?.userType === Entity.Customer);
 * ```
 */
const useFrontChat = (shouldShow: boolean = false) => {
  const hasInitializedRef = useRef(false);
  const visibilityIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );
  // Track current shouldShow value in a ref for use in async callbacks
  const shouldShowRef = useRef(shouldShow);
  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);

  // Keep shouldShowRef in sync with the prop
  useEffect(() => {
    shouldShowRef.current = shouldShow;
  }, [shouldShow]);

  // Inject or remove CSS to hide Front Chat
  const setCssVisibility = useCallback((show: boolean) => {
    const existingStyle = document.getElementById(FRONT_CHAT_HIDE_STYLE_ID);

    if (show) {
      // Remove hide CSS if it exists
      if (existingStyle) {
        existingStyle.remove();
      }
    } else {
      // Add hide CSS if it doesn't exist
      if (!existingStyle) {
        const style = document.createElement('style');
        style.id = FRONT_CHAT_HIDE_STYLE_ID;
        style.textContent = HIDE_FRONT_CHAT_CSS;
        document.head.appendChild(style);
      }
    }
  }, []);

  // Function to set visibility via Front Chat API - will retry until available
  const setApiVisibility = useCallback(
    (show: boolean, isCleanup: boolean = false) => {
      // Clear any existing interval
      if (visibilityIntervalRef.current) {
        clearInterval(visibilityIntervalRef.current);
        visibilityIntervalRef.current = null;
      }

      const applyVisibility = () => {
        // If this is not a cleanup call, check if the desired state is still current
        // This prevents stale show/hide calls from racing with newer state changes
        if (!isCleanup && shouldShowRef.current !== show) {
          // State changed since this call was initiated - abort
          return true; // Return true to stop polling
        }

        // Don't apply visibility if unmounted (except for cleanup hide)
        if (!isMountedRef.current && !isCleanup) {
          return true;
        }

        if (window.FrontChat) {
          try {
            window.FrontChat(show ? 'show' : 'hide');
          } catch (e) {
            console.error('Error setting Front Chat visibility:', e);
          }
          return true;
        }
        return false;
      };

      // Try immediately
      if (applyVisibility()) {
        return;
      }

      // If not ready, poll until it is (max 10 seconds)
      let attempts = 0;
      const maxAttempts = 100;
      visibilityIntervalRef.current = setInterval(() => {
        attempts++;
        if (applyVisibility() || attempts >= maxAttempts) {
          if (visibilityIntervalRef.current) {
            clearInterval(visibilityIntervalRef.current);
            visibilityIntervalRef.current = null;
          }
        }
      }, 100);
    },
    []
  );

  // Track mounted state
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Immediately apply CSS-based hiding on mount if shouldShow is false
  // This runs synchronously before the browser paints
  useEffect(() => {
    // Apply CSS visibility immediately - this is synchronous and prevents flash
    setCssVisibility(shouldShow);
  }, [shouldShow, setCssVisibility]);

  // Load and initialize script (only once)
  useEffect(() => {
    // Check if already initialized
    if (window.__frontChatInitialized) {
      // Script already initialized - trigger visibility effect to apply current state
      // Use setTimeout to ensure this runs after the visibility effect has set up
      setTimeout(() => {
        if (window.FrontChat && isMountedRef.current) {
          try {
            window.FrontChat(shouldShowRef.current ? 'show' : 'hide');
          } catch (e) {
            // Ignore errors
          }
        }
      }, 0);
      return;
    }

    // Check if script tag already exists
    const existingScript = document.querySelector(
      `script[src="${FRONT_CHAT_SCRIPT_URL}"]`
    );

    if (existingScript) {
      // Script exists but not initialized - wait for it to load
      // The onload will be handled by whoever added the script first
      return;
    }

    if (hasInitializedRef.current) {
      return;
    }

    hasInitializedRef.current = true;

    // Create and load the Front chat script
    const script = document.createElement('script');
    script.src = FRONT_CHAT_SCRIPT_URL;
    script.async = true;

    script.onload = () => {
      if (window.FrontChat && !window.__frontChatInitialized) {
        window.FrontChat('init', {
          chatId: FRONT_CHAT_ID,
          useDefaultLauncher: true,
        });
        window.__frontChatInitialized = true;

        // After init, apply the CURRENT visibility state (not always hide)
        // Use the ref to get the latest value
        if (isMountedRef.current) {
          window.FrontChat(shouldShowRef.current ? 'show' : 'hide');
        }
      }
    };

    script.onerror = () => {
      console.error('Failed to load Front chat script');
    };

    document.head.appendChild(script);
  }, []);

  // Control visibility via Front Chat API based on shouldShow parameter
  useEffect(() => {
    // Apply the current visibility state
    setApiVisibility(shouldShow);

    // Cleanup: only clear the interval
    // We DON'T hide the chat here because:
    // 1. If shouldShow changed, the next effect run will set the correct state
    // 2. If unmounting, the unmount cleanup effect handles it
    // This prevents the race condition where cleanup hides immediately before
    // the new effect can show
    return () => {
      if (visibilityIntervalRef.current) {
        clearInterval(visibilityIntervalRef.current);
        visibilityIntervalRef.current = null;
      }
    };
  }, [shouldShow, setApiVisibility]);

  // Cleanup on unmount - hide chat and clean up CSS
  useEffect(() => {
    return () => {
      // Clear any pending intervals
      if (visibilityIntervalRef.current) {
        clearInterval(visibilityIntervalRef.current);
        visibilityIntervalRef.current = null;
      }

      // Hide chat on unmount (cleanup call bypasses shouldShowRef check)
      if (window.FrontChat) {
        try {
          window.FrontChat('hide');
        } catch (e) {
          // Ignore errors during cleanup
        }
      }

      // Clean up CSS - remove the hide style so it doesn't interfere
      // with other components that might mount after
      const existingStyle = document.getElementById(FRONT_CHAT_HIDE_STYLE_ID);
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);
};

export default useFrontChat;
